import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle2, ArrowRight, Mail, Clock } from "lucide-react";
import { storeApi, formatPrice } from "@/lib/storefront";

export default function ThankYou() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const email = sp.get("email") || "";
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    storeApi.get(`/orders/${id}`, { params: { email } })
      .then((r) => setOrder(r.data))
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [id, email]);

  if (loading) return <div className="mx-auto max-w-2xl px-6 py-24"><div className="h-48 animate-pulse rounded-md bg-[#E8E2D2]" /></div>;
  if (!order) return (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <h1 className="font-display text-4xl">Order not found</h1>
      <Link to="/" className="mt-6 inline-block text-[#7D8471] underline">Back to shop</Link>
    </div>
  );

  const failed = order.payment_status === "failed" || order.status === "payment_error";
  const currency = order.currency || "USD";

  return (
    <div className="mx-auto max-w-3xl px-6 py-16 md:py-24" data-testid="thankyou-page">
      {/* Status banner */}
      <div className={`rounded-md border p-6 md:p-8 ${failed ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50"}`} data-testid={failed ? "status-failed" : "status-success"}>
        <div className="flex items-start gap-3">
          {failed ? <AlertCircle size={24} className="mt-0.5 text-red-600" /> : <CheckCircle2 size={24} className="mt-0.5 text-emerald-600" />}
          <div>
            <div className="font-display text-2xl md:text-3xl">
              {failed ? "Payment couldn't be processed" : "Thank you for your order"}
            </div>
            <p className="mt-2 text-sm text-[#2D2823] md:text-base">
              {failed ? (
                <>Your order <span className="font-mono font-semibold text-[#1A1A1A]">{order.order_number}</span> has been created, but the card was declined. Our team will reach out to <span className="font-medium text-[#1A1A1A]">{order.customer_email}</span> with alternative payment options within 24h.</>
              ) : (
                <>Your order <span className="font-mono font-semibold text-[#1A1A1A]">{order.order_number}</span> is confirmed. A receipt is on its way to <span className="font-medium text-[#1A1A1A]">{order.customer_email}</span>.</>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Order details */}
      <div className="mt-10">
        <h2 className="mb-4 font-display text-2xl">Order details</h2>
        <div className="rounded-md border border-[#DDD6C5] bg-white">
          <ul className="divide-y divide-[#DDD6C5]">
            {(order.items || []).map((it, i) => (
              <li key={i} className="flex items-center gap-4 p-4">
                {it.image ? <img src={it.image} alt="" className="h-16 w-12 rounded object-cover" /> : <div className="h-16 w-12 rounded bg-[#E8E2D2]" />}
                <div className="flex-1">
                  <div className="line-clamp-2 text-sm">{it.title}</div>
                  <div className="mt-0.5 text-xs text-[#2D2823] store-nums">Qty {it.quantity}</div>
                </div>
                <div className="text-sm store-nums">{formatPrice(it.subtotal, currency)}</div>
              </li>
            ))}
          </ul>
          <dl className="space-y-1.5 border-t border-[#DDD6C5] p-4 text-sm">
            <div className="flex justify-between"><dt className="text-[#2D2823]">Subtotal</dt><dd className="store-nums">{formatPrice(order.subtotal, currency)}</dd></div>
            <div className="flex justify-between"><dt className="text-[#2D2823]">Shipping ({order.shipping_method || "Standard"})</dt><dd className="store-nums">{formatPrice(order.shipping_cost || 0, currency)}</dd></div>
            {order.discount > 0 && <div className="flex justify-between"><dt className="text-[#2D2823]">Discount</dt><dd className="store-nums text-rose-600">−{formatPrice(order.discount, currency)}</dd></div>}
            <div className="flex items-baseline justify-between border-t border-[#DDD6C5] pt-3">
              <dt className="font-display text-lg">Total</dt>
              <dd className="font-display text-2xl store-nums">{formatPrice(order.total, currency)}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Next steps */}
      <div className="mt-8 grid gap-4 md:grid-cols-2">
        <div className="rounded-md border border-[#DDD6C5] bg-white p-5 text-sm">
          <Mail size={18} className="mb-2 text-[#7D8471]" />
          <div className="font-display text-base">Check your email</div>
          <p className="mt-1 text-[#2D2823]">Order confirmation sent to {order.customer_email}</p>
        </div>
        <div className="rounded-md border border-[#DDD6C5] bg-white p-5 text-sm">
          <Clock size={18} className="mb-2 text-[#7D8471]" />
          <div className="font-display text-base">Ships in 1-2 days</div>
          <p className="mt-1 text-[#2D2823]">Tracking will be emailed when your parcel leaves us</p>
        </div>
      </div>

      <div className="mt-12 text-center">
        <Link to="/catalog" className="inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] px-7 py-3.5 text-sm text-white hover:bg-[#7D8471]">
          Continue shopping <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
